/**
 * 按钮权限控制组件
 * 根据权限编码控制按钮的显示/隐藏
 */

import React from 'react';
import { Button, ButtonProps } from 'antd';
import { hasPermission } from '@/utils/permission';
import { useModel } from '@umijs/max';

export interface PermissionButtonProps extends ButtonProps {
  /**
   * 权限编码
   * 可以是单个权限码，也可以是权限码数组（需要全部拥有）
   * @example 'admin:create' 或 ['admin:create', 'admin:edit']
   */
  permission: string | string[];
  
  /**
   * 无权限时是否显示（但禁用）
   * @default false
   */
  showWhenNoPermission?: boolean;
  
  /**
   * 子元素（按钮内容）
   */
  children?: React.ReactNode;
}

/**
 * 权限按钮组件
 * 
 * @example
 * ```tsx
 * // 单个权限
 * <PermissionButton permission="admin:create" onClick={handleCreate}>
 *   新建管理员
 * </PermissionButton>
 * 
 * // 多个权限（需要全部拥有）
 * <PermissionButton permission={['admin:create', 'admin:edit']} onClick={handleAction}>
 *   操作
 * </PermissionButton>
 * 
 * // 无权限时显示但禁用
 * <PermissionButton 
 *   permission="admin:delete" 
 *   showWhenNoPermission 
 *   onClick={handleDelete}
 * >
 *   删除
 * </PermissionButton>
 * ```
 */
const PermissionButton: React.FC<PermissionButtonProps> = ({
  permission,
  showWhenNoPermission = false,
  children,
  ...buttonProps
}) => {
  const hasAuth = hasPermission(permission);
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState ?? {};

  // 超级管理员拥有所有权限
  if (currentUser?.isSuperAdmin) {
    return <Button {...buttonProps}>{children}</Button>;
  }

  // 如果没有权限且不显示，直接返回 null
  if (!hasAuth && !showWhenNoPermission) {
    return null;
  }

  // 如果没有权限但需要显示，返回禁用的按钮
  if (!hasAuth && showWhenNoPermission) {
    return (
      <Button {...buttonProps} disabled>
        {children}
      </Button>
    );
  }

  // 有权限，正常显示按钮
  return <Button {...buttonProps}>{children}</Button>;
};

export default PermissionButton;

